import type {IRoutesManager, OmitStrict} from '@wix/devzai-utils-common';
import {RoutesManager} from '@wix/devzai-utils-common';
import type {DoppeViewerPageInfo} from '../viewer-pages/doppe-viewer-pages';
import {DoppeViewerPageType} from '../viewer-pages/doppe-viewer-pages';

/**
 * Type helper to extract params from page info (excluding 'pageType' discriminator)
 */
export type DoppeViewerPageParams<PAGE_INFO extends DoppeViewerPageInfo> = OmitStrict<PAGE_INFO, 'pageType'>;

export const DoppeViewerCrossPagesQueryParams = [
    'petri_ovr', 'x-wix-route', 'demo-param', 'preview'
]

/**
 * First-segment URL paths reserved for vertical pages in the viewer.
 * Content pages must not use these as slugs — they would be unreachable
 * because vertical routes are matched before the content page catch-all.
 */
export const DoppeViewerReservedPageSlugs = [
    'products',
    'blog',
    'events',
    'services',
    'cart',
    'plans',
    'pricing-plans',
] as const;

/**
 * Viewer Pages Routes Manager
 * 
 * Manages bidirectional routing for the viewer (URL ↔ Page Info).
 * Uses constant paths for vertical pages and dynamic slugs for content pages.
 * 
 * ## URL Patterns
 * 
 * The viewer uses clean, constant URLs with the following patterns:
 * 
 * ```
 * Home:                   /
 * Content pages:          /:slug                              (e.g., /about, /contact)
 * 
 * Products list:          /products
 * Product details:        /products/product-details/:slug      (physical & digital)
 * 
 * Blog posts list:        /blog
 * Blog post details:      /blog/post-details/:slug
 * 
 * Events list:            /events
 * Event details:          /events/event-details/:slug          (RSVP & ticketing)
 * 
 * Services list:          /services
 * Service details:        /services/service-details/:slug      (appointment, class, course)
 * ```
 * 
 * ## Design Philosophy
 * 
 * - **Constant paths** for vertical pages (products, blog, events, services)
 * - **No page IDs in URLs** - page IDs resolved internally from default pages
 * - **Dynamic slugs** only for content pages and vertical items
 * - **Simple, predictable URLs** that are easy to remember and share
 * 
 * ## Route Registration Order
 * 
 * Routes are registered in specific-to-generic order to ensure correct matching:
 * 1. Home page (exact match: /)
 * 2. Item pages (/product/:slug, /post/:slug, etc.) - specific
 * 3. Category list pages (/products/categories/:slug, /blog/categories/:slug, etc.)
 * 4. List pages (/products, /blog, etc.) - constant paths
 * 5. Content pages (/:slug) - catch-all for custom content
 * 
 * @example
 * ```typescript
 * const routesManager = new DoppeViewerPagesRoutesManager({ basePath: '' });
 * 
 * // Generate URLs - no page IDs needed
 * routesManager.resolvePath({ pageType: 'ProductsList' });
 * // → '/products'
 * 
 * routesManager.resolvePath({ pageType: 'ProductItem', productSlug: 'my-product' });
 * // → '/product/my-product'
 * 
 * // Content pages still use slugs
 * routesManager.resolvePath({ pageType: 'ContentPage', slug: 'about' });
 * // → '/about'
 * ```
 */
export class DoppeViewerPagesRoutesManager implements IRoutesManager.Initialized<DoppeViewerPageInfo> {
    
    private routesManager: IRoutesManager<DoppeViewerPageInfo>;
    
    constructor(options: RoutesManager.Options = {}) {
        this.routesManager = RoutesManager.create<DoppeViewerPageInfo>(options);
        
        this.registerRoutes();
    }
    
    /**
     * Register a route with automatic param type inference
     * 
     * @template PAGE_INFO - The specific page info interface (e.g., DoppeViewerPageInfo.ProductItem)
     * @template PARAMS - URL route params (defaults to all PAGE_INFO fields except 'pageType')
     * @param options - Route configuration
     */
    private registerRoute<
        PAGE_INFO extends DoppeViewerPageInfo,
        PARAMS extends {} = DoppeViewerPageParams<PAGE_INFO>
    >(
        options: {
            path: string;
            pageType: PAGE_INFO['pageType'];
            createPageInfo: (params: PARAMS) => PAGE_INFO;
            extractParams?: (routeInfo: PAGE_INFO) => PARAMS;
        }
    ): void {
        const { path, pageType, createPageInfo, extractParams } = options;
        
        this.routesManager.registerRoute<PARAMS>({
            path: path,
            resolveRouteInfoFromParams: (match): PAGE_INFO => {
                return createPageInfo(match as PARAMS);
            },
            resolveParams: (routeInfo) => {
                if (routeInfo.pageType !== pageType) {
                    return null;
                }
                
                if (extractParams) {
                    // Use custom param extraction if provided
                    return extractParams(routeInfo as PAGE_INFO);
                } else {
                    // Default: Extract all properties except 'pageType'
                    const { pageType: _pageType, ...params } = routeInfo as PAGE_INFO;
                    return params as unknown as PARAMS;
                }
            }
        });
    }
    
    /**
     * Register all viewer routes
     */
    private registerRoutes(): void {
        
        // ========================================================================
        // Route Registration
        // Order: Specific routes first, then generic routes
        // ========================================================================
        
        // ------------------------------------------------------------------------
        // 1. Home Page
        // ------------------------------------------------------------------------
        
        this.registerRoute<DoppeViewerPageInfo.Home>({
            path: '/',
            pageType: DoppeViewerPageType.Home,
            createPageInfo: () => {
                return {
                    pageType: DoppeViewerPageType.Home
                };
            }
        });
        
        // ------------------------------------------------------------------------
        // 2. Unified Item Pages (preferred routes)
        // Each vertical has a single canonical item route regardless of subtype.
        // Template selection (e.g., physical vs digital product) happens at render time.
        // Registered before legacy per-subtype routes so resolvePath prefers these.
        // ------------------------------------------------------------------------
        
        // Unified Product Details: /products/product-details/:productSlug
        // Handles both physical and digital products at the same path.
        this.routesManager.registerRoute<{ productSlug: string }>({
            path: '/products/product-details(/:productSlug)',
            resolveRouteInfoFromParams: (match): DoppeViewerPageInfo => {
                return {
                    pageType: DoppeViewerPageType.ProductItem,
                    productSlug: match.productSlug ?? ''
                };
            },
            resolveParams: (routeInfo) => {
                if (routeInfo.pageType === DoppeViewerPageType.ProductItem) {
                    return {productSlug: routeInfo.productSlug};
                }
                if (routeInfo.pageType === DoppeViewerPageType.DigitalProductItem) {
                    return {productSlug: routeInfo.productSlug ?? ''};
                }
                return null;
            }
        });

        // Unified Event Details: /events/event-details/:eventSlug
        // Handles both RSVP and ticketing events at the same path.
        this.routesManager.registerRoute<{ eventSlug: string }>({
            path: '/events/event-details(/:eventSlug)',
            resolveRouteInfoFromParams: (match): DoppeViewerPageInfo => {
                return {
                    pageType: DoppeViewerPageType.EventItem,
                    eventSlug: match.eventSlug ?? ''
                };
            },
            resolveParams: (routeInfo) => {
                if (routeInfo.pageType === DoppeViewerPageType.EventItem) {
                    return {eventSlug: routeInfo.eventSlug ?? ''};
                }
                if (routeInfo.pageType === DoppeViewerPageType.TicketingEventItem) {
                    return {eventSlug: routeInfo.eventSlug ?? ''};
                }
                return null;
            }
        });

        // Unified Service Details: /services/service-details/:serviceSlug
        // Handles appointments, classes, courses, and generic services at the same path.
        this.routesManager.registerRoute<{ serviceSlug: string }>({
            path: '/services/service-details(/:serviceSlug)',
            resolveRouteInfoFromParams: (match): DoppeViewerPageInfo => {
                return {
                    pageType: DoppeViewerPageType.ServiceItem,
                    serviceSlug: match.serviceSlug ?? ''
                };
            },
            resolveParams: (routeInfo) => {
                if (routeInfo.pageType === DoppeViewerPageType.ServiceItem
                    || routeInfo.pageType === DoppeViewerPageType.CourseItem
                    || routeInfo.pageType === DoppeViewerPageType.AppointmentItem
                    || routeInfo.pageType === DoppeViewerPageType.ClassItem) {
                    return {serviceSlug: (routeInfo as DoppeViewerPageInfo.ServiceItem).serviceSlug};
                }
                return null;
            }
        });

        // Unified Blog Post Details: /blog/post-details/:postSlug
        this.routesManager.registerRoute<{ postSlug: string }>({
            path: '/blog/post-details(/:postSlug)',
            resolveRouteInfoFromParams: (match): DoppeViewerPageInfo => {
                return {
                    pageType: DoppeViewerPageType.BlogPostItem,
                    postSlug: match.postSlug ?? ''
                };
            },
            resolveParams: (routeInfo) => {
                if (routeInfo.pageType === DoppeViewerPageType.BlogPostItem) {
                    return {postSlug: routeInfo.postSlug ?? ''};
                }
                return null;
            }
        });

        // ------------------------------------------------------------------------
        // 2b. Legacy Item Pages (deprecated — kept for backward-compatible URL parsing)
        // These routes still resolve incoming URLs like /digital-product/:slug,
        // but resolvePath will prefer the unified routes above for URL generation.
        // ------------------------------------------------------------------------
        
        /** @deprecated Use /products/product-details/:productSlug instead */
        this.registerRoute<DoppeViewerPageInfo.ProductItem, { productSlug: string }>({
            path: '/product(/:productSlug)',
            pageType: DoppeViewerPageType.ProductItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.ProductItem,
                    productSlug: params.productSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    productSlug: routeInfo.productSlug
                };
            }
        });
        
        /** @deprecated Use /products/product-details/:productSlug instead */
        this.registerRoute<DoppeViewerPageInfo.DigitalProductItem, { productSlug: string }>({
            path: '/digital-product(/:productSlug)',
            pageType: DoppeViewerPageType.DigitalProductItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.DigitalProductItem,
                    productSlug: params.productSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    productSlug: routeInfo.productSlug ?? ''
                };
            }
        });
        
        /** @deprecated Use /blog/post-details/:postSlug instead */
        this.registerRoute<DoppeViewerPageInfo.BlogPostItem, { postSlug: string }>({
            path: '/post(/:postSlug)',
            pageType: DoppeViewerPageType.BlogPostItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.BlogPostItem,
                    postSlug: params.postSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    postSlug: routeInfo.postSlug ?? ''
                };
            }
        });
        
        /** @deprecated Use /events/event-details/:eventSlug instead */
        this.registerRoute<DoppeViewerPageInfo.EventItem, { eventSlug: string }>({
            path: '/rsvp-event(/:eventSlug)',
            pageType: DoppeViewerPageType.EventItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.EventItem,
                    eventSlug: params.eventSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    eventSlug: routeInfo.eventSlug ?? ''
                };
            }
        });
        
        /** @deprecated Use /events/event-details/:eventSlug instead */
        this.registerRoute<DoppeViewerPageInfo.TicketingEventItem, { eventSlug: string }>({
            path: '/ticket-event(/:eventSlug)',
            pageType: DoppeViewerPageType.TicketingEventItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.TicketingEventItem,
                    eventSlug: params.eventSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    eventSlug: routeInfo.eventSlug ?? ''
                };
            }
        });
        
        /** @deprecated Use /services/service-details/:serviceSlug instead */
        this.registerRoute<DoppeViewerPageInfo.ServiceItem, { serviceSlug: string }>({
            path: '/service(/:serviceSlug)',
            pageType: DoppeViewerPageType.ServiceItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.ServiceItem,
                    serviceSlug: params.serviceSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    serviceSlug: routeInfo.serviceSlug
                };
            }
        });
        
        /** @deprecated Use /services/service-details/:serviceSlug instead */
        this.registerRoute<DoppeViewerPageInfo.CourseItem, { serviceSlug: string }>({
            path: '/course(/:serviceSlug)',
            pageType: DoppeViewerPageType.CourseItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.CourseItem,
                    serviceSlug: params.serviceSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    serviceSlug: routeInfo.serviceSlug
                };
            }
        });
        
        /** @deprecated Use /services/service-details/:serviceSlug instead */
        this.registerRoute<DoppeViewerPageInfo.AppointmentItem, { serviceSlug: string }>({
            path: '/appointment(/:serviceSlug)',
            pageType: DoppeViewerPageType.AppointmentItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.AppointmentItem,
                    serviceSlug: params.serviceSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    serviceSlug: routeInfo.serviceSlug
                };
            }
        });
        
        /** @deprecated Use /services/service-details/:serviceSlug instead */
        this.registerRoute<DoppeViewerPageInfo.ClassItem, { serviceSlug: string }>({
            path: '/class(/:serviceSlug)',
            pageType: DoppeViewerPageType.ClassItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.ClassItem,
                    serviceSlug: params.serviceSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    serviceSlug: routeInfo.serviceSlug
                };
            }
        });
        
        // ------------------------------------------------------------------------
        // 3. Category List Pages (more specific than plain list pages)
        // Pattern: /{vertical}/categories/:categorySlug
        // These filter the list by a category/collection slug.
        // ------------------------------------------------------------------------
        
        // Products by category: /products/categories/:categorySlug
        this.registerRoute<DoppeViewerPageInfo.ProductsList, { categorySlug: string }>({
            path: '/products/categories/:categorySlug',
            pageType: DoppeViewerPageType.ProductsList,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.ProductsList,
                    categorySlug: params.categorySlug
                };
            },
            extractParams: (routeInfo) => {
                if (!routeInfo.categorySlug) {
                    return null as any;
                }
                return {
                    categorySlug: routeInfo.categorySlug
                };
            }
        });
        
        // Blog posts by category: /blog/categories/:categorySlug
        this.registerRoute<DoppeViewerPageInfo.BlogPostsList, { categorySlug: string }>({
            path: '/blog/categories/:categorySlug',
            pageType: DoppeViewerPageType.BlogPostsList,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.BlogPostsList,
                    categorySlug: params.categorySlug
                };
            },
            extractParams: (routeInfo) => {
                if (!routeInfo.categorySlug) {
                    return null as any;
                }
                return {
                    categorySlug: routeInfo.categorySlug
                };
            }
        });
        
        // Events by category: /events/categories/:categorySlug
        this.registerRoute<DoppeViewerPageInfo.EventsList, { categorySlug: string }>({
            path: '/events/categories/:categorySlug',
            pageType: DoppeViewerPageType.EventsList,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.EventsList,
                    categorySlug: params.categorySlug
                };
            },
            extractParams: (routeInfo) => {
                if (!routeInfo.categorySlug) {
                    return null as any;
                }
                return {
                    categorySlug: routeInfo.categorySlug
                };
            }
        });
        
        // Services by category: /services/categories/:categorySlug
        this.registerRoute<DoppeViewerPageInfo.ServicesList, { categorySlug: string }>({
            path: '/services/categories/:categorySlug',
            pageType: DoppeViewerPageType.ServicesList,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.ServicesList,
                    categorySlug: params.categorySlug
                };
            },
            extractParams: (routeInfo) => {
                if (!routeInfo.categorySlug) {
                    return null as any;
                }
                return {
                    categorySlug: routeInfo.categorySlug
                };
            }
        });
        
        // ------------------------------------------------------------------------
        // 4. List Pages (Constant paths - registered before content pages)
        // Plain list pages without category filter.
        // ------------------------------------------------------------------------
        
        // Products List: /products
        this.registerRoute<DoppeViewerPageInfo.ProductsList, {}>({
            path: '/products',
            pageType: DoppeViewerPageType.ProductsList,
            createPageInfo: () => {
                return {
                    pageType: DoppeViewerPageType.ProductsList
                };
            },
            extractParams: (routeInfo) => {
                return routeInfo.categorySlug ? null as any : {};
            }
        });
        
        // Blog Posts List: /blog
        this.registerRoute<DoppeViewerPageInfo.BlogPostsList, {}>({
            path: '/blog',
            pageType: DoppeViewerPageType.BlogPostsList,
            createPageInfo: () => {
                return {
                    pageType: DoppeViewerPageType.BlogPostsList
                };
            },
            extractParams: (routeInfo) => {
                return routeInfo.categorySlug ? null as any : {};
            }
        });
        
        // Events List: /events
        this.registerRoute<DoppeViewerPageInfo.EventsList, {}>({
            path: '/events',
            pageType: DoppeViewerPageType.EventsList,
            createPageInfo: () => {
                return {
                    pageType: DoppeViewerPageType.EventsList
                };
            },
            extractParams: (routeInfo) => {
                return routeInfo.categorySlug ? null as any : {};
            }
        });
        
        // Services List: /services
        this.registerRoute<DoppeViewerPageInfo.ServicesList, {}>({
            path: '/services',
            pageType: DoppeViewerPageType.ServicesList,
            createPageInfo: () => {
                return {
                    pageType: DoppeViewerPageType.ServicesList
                };
            },
            extractParams: (routeInfo) => {
                return routeInfo.categorySlug ? null as any : {};
            }
        });

        // Cart Page: /cart
        this.registerRoute<DoppeViewerPageInfo.Cart>({
            path: '/cart',
            pageType: DoppeViewerPageType.Cart,
            createPageInfo: () => {
                return {
                    pageType: DoppeViewerPageType.Cart
                };
            }
        });
        
        // ------------------------------------------------------------------------
        // 4. Content Pages (Generic - registered last)
        // Pattern: /:slug for custom content pages like /about, /contact
        // ------------------------------------------------------------------------
        
        // Content Page: /:slug (catch-all for content pages)
        // Note: pageId can be looked up from slug using bymoPagePropsGetInnerPageBySlug()
        this.registerRoute<DoppeViewerPageInfo.ContentPage, { slug: string }>({
            path: '/:slug',
            pageType: DoppeViewerPageType.ContentPage,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.ContentPage,
                    slug: params.slug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    slug: routeInfo.slug
                };
            }
        });
    }
    
    // ========================================================================
    // Core Methods (delegating to routes manager)
    // ========================================================================
    
    /**
     * Generate a URL path from page info
     * @throws Error if no route matches the page info
     */
    public resolvePath(routeInfo: DoppeViewerPageInfo): string {
        return this.routesManager.resolvePath(routeInfo);
    }
    
    /**
     * Parse a URL path to page info
     * 
     * NOTE: Due to multiple routes sharing the same URL pattern,
     * this method cannot reliably distinguish between different page types
     * (e.g., ProductsList vs ContentPage both use /page/:slug).
     * 
     * Use DoppeViewerRouterClientService.resolvePageInfoFromUrl() instead,
     * which considers bymoPageProps context to determine the correct type.
     * 
     * @returns Page info if route matches, null otherwise
     */
    public resolveRouteInfo(path: string, query: Record<string, string> | string): DoppeViewerPageInfo | null {
        return this.routesManager.resolveRouteInfo(path, query);
    }
    
    /**
     * Safely generate a URL path from page info
     * @returns URL path or null if no route matches
     */
    public tryResolvePath(routeInfo: DoppeViewerPageInfo): string | null {
        return this.routesManager.tryResolvePath(routeInfo);
    }
    
    // ========================================================================
    // Helper Methods (type-safe URL generation)
    // ========================================================================
    
    /**
     * Generate URL for home page
     * @returns URL path (e.g., '/')
     */
    public homePage(): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.Home
        });
    }
    
    /**
     * Generate URL for content page
     * @param options - Page parameters
     * @returns URL path (e.g., '/about')
     */
    public contentPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.ContentPage>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ContentPage,
            slug: options.slug
        });
    }
    
    /**
     * Generate URL for products list page
     * @returns URL path (e.g., '/products')
     */
    public productsListPage(): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ProductsList
        });
    }
    
    /**
     * Generate URL for products list page filtered by category
     * @param categorySlug - The category/collection slug
     * @returns URL path (e.g., '/products/categories/shoes')
     */
    public productsListCategoryPage(categorySlug: string): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ProductsList,
            categorySlug: categorySlug
        });
    }
    
    /**
     * Generate URL for the unified product details page.
     * Handles both physical and digital products at the same path.
     * @param options - Page parameters (optional slug, defaults to first product)
     * @returns URL path (e.g., '/products/product-details/my-product')
     */
    public productDetailsPage(options: { productSlug?: string }): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ProductItem,
            productSlug: options.productSlug ?? ''
        });
    }

    /**
     * Generate URL for the unified event details page.
     * Handles both RSVP and ticketing events at the same path.
     * @param options - Page parameters (optional slug, defaults to first event)
     * @returns URL path (e.g., '/events/event-details/conference-2024')
     */
    public eventDetailsPage(options: { eventSlug?: string }): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.EventItem,
            eventSlug: options.eventSlug ?? ''
        });
    }

    /**
     * Generate URL for the unified service details page.
     * Handles appointments, classes, courses, and generic services at the same path.
     * @param options - Page parameters (optional slug, defaults to first service)
     * @returns URL path (e.g., '/services/service-details/consultation')
     */
    public serviceDetailsPage(options: { serviceSlug?: string }): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ServiceItem,
            serviceSlug: options.serviceSlug ?? ''
        });
    }

    /**
     * Generate URL for the unified blog post details page.
     * @param options - Page parameters (optional slug, defaults to first post)
     * @returns URL path (e.g., '/blog/post-details/my-post')
     */
    public postDetailsPage(options: { postSlug?: string }): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.BlogPostItem,
            postSlug: options.postSlug ?? ''
        });
    }

    /**
     * @deprecated Use {@link productDetailsPage} instead. This resolves to the legacy /product/:slug path.
     *
     * Generate URL for product item page
     * @param options - Page parameters (optional slug, defaults to first product)
     * @returns URL path (e.g., '/product/my-product' or '/product')
     */
    public productItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.ProductItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ProductItem,
            productSlug: options.productSlug
        });
    }
    
    /**
     * Generate URL for blog posts list page
     * @returns URL path (e.g., '/blog')
     */
    public blogPostsListPage(): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.BlogPostsList
        });
    }
    
    /**
     * Generate URL for blog posts list page filtered by category
     * @param categorySlug - The blog category slug
     * @returns URL path (e.g., '/blog/categories/tech')
     */
    public blogPostsListCategoryPage(categorySlug: string): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.BlogPostsList,
            categorySlug: categorySlug
        });
    }
    
    /**
     * @deprecated Use {@link postDetailsPage} instead. This resolves to the legacy /post/:slug path.
     */
    public blogPostItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.BlogPostItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.BlogPostItem,
            postSlug: options.postSlug
        });
    }
    
    /**
     * Generate URL for events list page
     * @returns URL path (e.g., '/events')
     */
    public eventsListPage(): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.EventsList
        });
    }
    
    /**
     * Generate URL for events list page filtered by category
     * @param categorySlug - The event category slug
     * @returns URL path (e.g., '/events/categories/music')
     */
    public eventsListCategoryPage(categorySlug: string): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.EventsList,
            categorySlug: categorySlug
        });
    }
    
    /**
     * @deprecated Use {@link eventDetailsPage} instead. This resolves to the legacy /rsvp-event/:slug path.
     */
    public eventItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.EventItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.EventItem,
            eventSlug: options.eventSlug
        });
    }
    
    /**
     * Generate URL for services list page
     * @returns URL path (e.g., '/services')
     */
    public servicesListPage(): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ServicesList
        });
    }
    
    /**
     * Generate URL for services list page filtered by category
     * @param categorySlug - The service category slug
     * @returns URL path (e.g., '/services/categories/fitness')
     */
    public servicesListCategoryPage(categorySlug: string): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ServicesList,
            categorySlug: categorySlug
        });
    }
    
    /**
     * @deprecated Use {@link serviceDetailsPage} instead. This resolves to the legacy /service/:slug path.
     */
    public serviceItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.ServiceItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ServiceItem,
            serviceSlug: options.serviceSlug
        });
    }
    
    /**
     * @deprecated Use {@link serviceDetailsPage} instead. This resolves to the legacy /course/:slug path.
     */
    public courseItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.CourseItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.CourseItem,
            serviceSlug: options.serviceSlug
        });
    }
    
    /**
     * @deprecated Use {@link serviceDetailsPage} instead. This resolves to the legacy /appointment/:slug path.
     */
    public appointmentItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.AppointmentItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.AppointmentItem,
            serviceSlug: options.serviceSlug
        });
    }
    
    /**
     * @deprecated Use {@link serviceDetailsPage} instead. This resolves to the legacy /class/:slug path.
     */
    public classItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.ClassItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ClassItem,
            serviceSlug: options.serviceSlug
        });
    }

    /**
     * @deprecated Use {@link productDetailsPage} instead. This resolves to the legacy /digital-product/:slug path.
     */
    public digitalProductItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.DigitalProductItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.DigitalProductItem,
            productSlug: options.productSlug
        });
    }

    /**
     * @deprecated Use {@link eventDetailsPage} instead. This resolves to the legacy /ticket-event/:slug path.
     */
    public ticketingEventItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.TicketingEventItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.TicketingEventItem,
            eventSlug: options.eventSlug
        });
    }

    /**
     * Generate URL for cart page
     * @returns URL path (e.g., '/cart')
     */
    public cartPage(): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.Cart
        });
    }
}
